import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, ScanBarcode, Package, ImageOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { idr } from '@/components/erp/Erp';

/** Left column: product grid with search/barcode + category filter. */
export const ProductGrid = ({ call, onPick, warehouseId, id }) => {
  const [data, setData] = useState({ products: [], categories: [] });
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    call('get', `/pos/products?q=${encodeURIComponent(q)}&category_id=${cat}&warehouse_id=${warehouseId || ''}`).then((d) => alive && setData(d)).catch(() => {}).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [call, q, cat, warehouseId]);

  const scan = async (e) => {
    if (e.key !== 'Enter' || !q.trim()) return;
    e.preventDefault();
    const d = await call('get', `/pos/products?barcode=${encodeURIComponent(q.trim())}&warehouse_id=${warehouseId || ''}`);
    const exact = d.products[0] || (data.products.length === 1 ? data.products[0] : null);
    if (exact) { onPick(exact); setQ(''); inputRef.current?.focus(); }
  };

  const cats = useMemo(() => [{ id: '', name: id ? 'Semua' : 'All' }, ...data.categories], [data.categories, id]);

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="pos-products">
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input ref={inputRef} autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={scan} placeholder={id ? 'Cari nama / SKU, atau scan barcode lalu Enter…' : 'Search name / SKU or scan barcode + Enter…'} className="rounded-xl pl-9 pr-9 h-11" data-testid="pos-search" />
        <ScanBarcode size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
      </div>
      <div className="mt-3 flex gap-1.5 overflow-x-auto pb-1" data-testid="pos-categories">
        {cats.map((c) => <button key={c.id || 'all'} type="button" onClick={() => setCat(c.id)} className={`shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${cat === c.id ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-white text-muted-foreground hover:text-foreground'}`} data-testid={`pos-cat-${c.id || 'all'}`}>{c.name}</button>)}
      </div>
      <div className="mt-3 grid flex-1 content-start gap-2.5 overflow-y-auto pr-1 grid-cols-2 sm:grid-cols-3 xl:grid-cols-4" data-testid="pos-product-grid">
        {loading && data.products.length === 0 && Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-40 animate-pulse rounded-2xl bg-muted" />)}
        {!loading && data.products.length === 0 && <div className="col-span-full flex flex-col items-center gap-2 py-16 text-sm text-muted-foreground"><Package size={28} />{id ? 'Tidak ada produk tersedia (aktif & stok > 0).' : 'No products available (active & in stock).'}</div>}
        {data.products.map((p) => (
          <button key={p.id} type="button" onClick={() => onPick(p)} className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-white text-left shadow-soft transition-transform hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98]" data-testid={`pos-product-${p.id}`}>
            <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
              {p.image_url ? <img src={p.image_url} alt={p.name} className="h-full w-full object-cover transition-transform group-hover:scale-105" loading="lazy" /> : <div className="flex h-full items-center justify-center text-muted-foreground/60"><ImageOff size={22} /></div>}
            </div>
            <div className="flex flex-1 flex-col px-3 py-2">
              <div className="line-clamp-2 text-sm font-medium leading-snug">{p.name}</div>
              {p.variants?.length > 0 && <span className="mt-0.5 w-fit rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground" data-testid={`pos-product-variants-${p.id}`}>{p.variants.length} {id ? 'varian' : 'variants'}</span>}
              <div className="mt-auto flex items-end justify-between pt-1.5">
                <span className="font-display text-sm font-semibold text-primary">{idr(p.price)}</span>
                {p.stock !== null && <span className={`text-[11px] ${p.stock <= 3 ? 'text-amber-700' : 'text-muted-foreground'}`}>{id ? 'Stok' : 'Stock'} {p.stock}</span>}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
